import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authApi, setAuthTokens } from '../services/api';
import type { AuthState, User, AuthTokens } from '../types';

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  register: (userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password_confirm: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'LOGIN_ERROR' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'LOGIN_ERROR':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
      };
    
    case 'LOGOUT':
      return {
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
      };
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [skipAuthCheck, setSkipAuthCheck] = React.useState(false);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      console.log('🔐 Iniciando login SEGURO...');
      dispatch({ type: 'LOGIN_START' });

      // Limpar tokens antigos
      localStorage.removeItem('authTokens');
      
      console.log('📡 Solicitando login...');
      const response = await authApi.loginNew(username, password);
      console.log('✅ Login bem-sucedido:', response.message);
      
      // Salvar tokens - mas com validação de mudança de senha
      setAuthTokens(response.tokens);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, tokens: response.tokens },
      });
      
      console.log('✅ Login completado com tokens atualizados!');
    } catch (error) {
      console.error('❌ Erro no login:', error);
      dispatch({ type: 'LOGIN_ERROR' });
      setAuthTokens(null);
      throw error;
    }
  };

  const register = async (userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password_confirm: string;
  }): Promise<void> => {
    try {
      console.log('📝 Iniciando registro...');
      dispatch({ type: 'LOGIN_START' });

      const response = await authApi.register(userData);
      console.log('✅ Registro bem-sucedido:', response.message);
      
      setAuthTokens(response.tokens);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, tokens: response.tokens },
      });
      console.log('✅ Registro e login completados!');
    } catch (error) {
      console.error('❌ Erro no registro:', error);
      dispatch({ type: 'LOGIN_ERROR' });
      setAuthTokens(null);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Try to logout via API first
      if (state.tokens?.refresh) {
        await authApi.logout(state.tokens.refresh);
      }
    } catch (error) {
      console.warn('Erro ao fazer logout no servidor:', error);
    } finally {
      // Limpar tokens de autenticação
      localStorage.removeItem('authTokens');
      setAuthTokens(null);
      dispatch({ type: 'LOGOUT' });
      console.log('🔐 Logout completo');
    }
  };

  const checkAuthStatus = useCallback(async (): Promise<void> => {
    try {
      console.log('🔍 Verificando status de autenticação...');
      dispatch({ type: 'SET_LOADING', payload: true });

      const storedTokens = localStorage.getItem('authTokens');
      if (!storedTokens) {
        console.log('❌ Nenhum token encontrado');
        dispatch({ type: 'LOGIN_ERROR' });
        return;
      }

      const tokenData = JSON.parse(storedTokens);
      const tokens = { access: tokenData.access, refresh: tokenData.refresh };

      // SEMPRE verificar com o servidor se o token é válido
      console.log('🔍 Validando token com servidor...');
      await authApi.verify(tokens.access);
      console.log('✅ Token válido no servidor');

      // Buscar usuário atual para garantir dados atualizados
      const user = await authApi.getCurrentUser();
      console.log('✅ Dados do usuário atualizados');
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens },
      });
    } catch (error) {
      console.log('❌ Token inválido - removendo e redirecionando');
      localStorage.removeItem('authTokens');
      setAuthTokens(null);
      dispatch({ type: 'LOGIN_ERROR' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;